<template>
  <div class="main-layout">
    <header class="topbar">
      <div class="topbar-inner">
        <router-link to="/" class="brand">
          <span class="brand-mark">{{ $t('layout.brand.mark') }}</span>
          <span class="brand-text">{{ $t('layout.brand.name') }}</span>
        </router-link>
        <div class="nav-container" :class="{ 'nav-container--scrollable': navHasOverflow }">
          <button
            v-if="navHasOverflow"
            type="button"
            class="nav-scroll nav-scroll--left"
            :disabled="navAtStart"
            @click="scrollNav(-1)"
            aria-label="Scroll navigation left"
          >
            ‹
          </button>
          <nav
            ref="navMain"
            class="nav-main"
            :class="navMainClasses"
          >
            <router-link to="/strategy/performance" v-if="isLoggedIn">{{ $t('layout.nav.strategy') }}</router-link>
            <router-link to="/trading/entry" v-if="isLoggedIn">{{ $t('layout.nav.entry') }}</router-link>
            <router-link to="/trading/exit" v-if="isLoggedIn">{{ $t('layout.nav.exit') }}</router-link>
            <router-link to="/collections" v-if="isLoggedIn">{{ $t('layout.nav.collections') }}</router-link>
            <router-link to="/simulation/history" v-if="isLoggedIn && showBacktestFeature">{{ $t('layout.nav.backtest') }}</router-link>
            <router-link to="/subscription/plans">{{ $t('layout.nav.subscription') }}</router-link>
            <router-link to="/invitation/manage" v-if="isLoggedIn">{{ $t('layout.nav.invitation') }}</router-link>
            <router-link to="/payment/history" v-if="isLoggedIn">{{ $t('layout.nav.paymentHistory') }}</router-link>
            <router-link v-if="showHelpLink" to="/help">{{ $t('layout.nav.help') }}</router-link>
            <router-link to="/feedback">{{ $t('layout.nav.feedback') }}</router-link>
          </nav>
          <button
            v-if="navHasOverflow"
            type="button"
            class="nav-scroll nav-scroll--right"
            :disabled="navAtEnd"
            @click="scrollNav(1)"
            aria-label="Scroll navigation right"
          >
            ›
          </button>
        </div>
        <div class="promo-pill" role="status" aria-live="polite">
          <span class="promo-pill__icon" aria-hidden="true">🎊</span>
          <span class="promo-pill__text">{{ $t('layout.banner.freeTrial') }}</span>
        </div>
        <div class="nav-right">
          <div class="language-switcher">
            <label class="sr-only" :for="languageSelectId">{{ $t('layout.language.label') }}</label>
            <select :id="languageSelectId" :value="currentLocale" @change="handleLocaleChange">
              <option v-for="option in localeOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
          <template v-if="isLoggedIn">
            <router-link to="/notifications" class="notification-link">
              {{ $t('layout.nav.notifications') }}
              <span v-if="unreadNotificationCount > 0" class="notification-count">{{ unreadNotificationCount }}</span>
            </router-link>
            <router-link to="/profile" v-if="userEmail" class="profile-link">{{ userEmail || $t('layout.nav.profilePlaceholder') }}</router-link>
            <button @click="handleLogout" class="logout-button">{{ $t('layout.nav.logout') }}</button>
          </template>
          <template v-else>
            <router-link to="/auth" class="auth-link">{{ $t('layout.nav.loginOrRegister') }}</router-link>
          </template>
        </div>
      </div>
    </header>
    <main>
      <RouterView />
    </main>
    <footer class="site-footer">
      <div class="site-footer-inner">
        <p class="footer-copy">{{ $t('layout.footer.copyright') }}</p>
        <div class="footer-links">
          <router-link to="/about" class="footer-link">{{ $t('layout.footer.about') }}</router-link>
          <router-link v-if="showFaqLink" to="/help" class="footer-link">{{ $t('layout.footer.faq') }}</router-link>
          <router-link to="/terms-of-service" class="footer-link">{{ $t('layout.footer.terms') }}</router-link>
          <router-link to="/refund-policy" class="footer-link">{{ $t('layout.footer.refund') }}</router-link>
          <router-link to="/privacy-policy" class="footer-link">{{ $t('layout.footer.privacy') }}</router-link>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { getUnreadNotificationCount } from '@/services/api';
import { useAuthStore } from '@/stores/authStore'; // Import the auth store
import { useLocaleStore } from '@/stores/localeStore';
// import { useCasdoor } from 'casdoor-vue-sdk'; // Cannot use Composition API here directly with Vue 2 Options API
// import { useCasdoor } from 'casdoor-vue-sdk'; // For full Casdoor logout if needed later

export default {
  name: 'MainLayout',
  data() {
    return {
      unreadNotificationCount: 0,
      notificationPollInterval: null,
      languageSelectId: 'language-select',
      navMainEl: null,
      navHasOverflow: false,
      navAtStart: true,
      navAtEnd: true,
      boundNavScrollHandler: null,
      boundNavResizeHandler: null,
      showHelpLink: false,
      showFaqLink: false
    };
  },
  // data() { // Replaced by computed properties where possible
  //   return {
  //     // isLoggedIn is now a computed property
  //   };
  // },
  computed: {
    isAuthenticated() { // Renamed for clarity, or keep isLoggedIn if preferred template variable name
      const auth = useAuthStore();
      return auth.isAuthenticated;
    },
    // isLoggedIn is now a computed property based on store's isAuthenticated
    isLoggedIn() { // Keep this if template uses isLoggedIn
      const auth = useAuthStore();
      return auth.isAuthenticated;
    },
    userName() { // Using userName from store, which can be configured to return name/email
      const auth = useAuthStore();
      return auth.userName;
    },
    // userEmail can be replaced by userName or a more specific getter if needed
    userEmail() { // Keep this if template uses userEmail
      const auth = useAuthStore();
      // If auth.userName is configured to return the display name (e.g., user.name)
      // and the template specifically needs email, you might need auth.currentUser.email
      return auth.userName; // Or auth.currentUser?.email || auth.userName for fallback
    },
    showBacktestFeature() {
      return false;
    },
    currentLocale() {
      const localeStore = useLocaleStore();
      return localeStore.currentLocale;
    },
    navMainClasses() {
      const classes = [];
      if (this.navHasOverflow) {
        classes.push('nav-main--scrollable');
      }
      if (this.navAtStart) {
        classes.push('nav-main--at-start');
      }
      if (this.navAtEnd) {
        classes.push('nav-main--at-end');
      }
      return classes;
    },
    localeOptions() {
      const localeStore = useLocaleStore();
      return localeStore.availableLocales.map((value) => ({
        value,
        label:
          value === 'zh-CN'
            ? this.$t('layout.language.zhCN')
            : this.$t('layout.language.enUS')
      }));
    }
  },
  methods: {
    handleLogout() {
      console.log('MainLayout: handleLogout called (using authStore)');
      const auth = useAuthStore();
      auth.logout(); // This will clear state and localStorage

      // Redirect to login page or home page
      if (this.$route.path !== '/auth') {
        this.$router.push('/auth');
      }
      // No need to manually update UI, store reactivity should handle it
    },
    async fetchUnreadCount() {
      // Use the store's isAuthenticated getter for the condition
      const auth = useAuthStore();
      if (!auth.isAuthenticated) return;
      try {
        const count = await getUnreadNotificationCount(); // Removed 'mockUser1' argument
        if (typeof count === 'number' && count >= 0) {
          this.unreadNotificationCount = count;
        } else {
          // This case might occur if API returns something unexpected but not an error
          console.warn('Could not fetch unread notification count: API returned unexpected data type or value.', count);
          this.unreadNotificationCount = 0; // Optionally reset or keep old value
        }
      } catch (error) {
        console.error('Error fetching unread notification count:', error);
        // Optionally set unreadNotificationCount to 0 or a specific error indicator on error
        this.unreadNotificationCount = 0;
      }
    },
    startNotificationPolling() {
      this.stopNotificationPolling(); // Clear existing interval
      if (this.isLoggedIn) {
        this.fetchUnreadCount(); // Initial fetch
        this.notificationPollInterval = setInterval(this.fetchUnreadCount, 1800000); // Poll every 1800 seconds (30 minutes)
      }
    },
    stopNotificationPolling() {
      if (this.notificationPollInterval) {
        clearInterval(this.notificationPollInterval);
        this.notificationPollInterval = null;
      }
    },
    handleLocaleChange(event) {
      const localeStore = useLocaleStore();
      localeStore.setLocale(event.target.value);
      this.deferNavReflowCheck();
    },
    setupNavOverflowHandling() {
      this.$nextTick(() => {
        if (typeof window === 'undefined') return;
        this.navMainEl = this.$refs.navMain;
        if (!this.navMainEl) return;
        if (!this.boundNavScrollHandler) {
          this.boundNavScrollHandler = this.handleNavScroll.bind(this);
        }
        if (!this.boundNavResizeHandler) {
          this.boundNavResizeHandler = this.checkNavOverflow.bind(this);
        }
        this.navMainEl.addEventListener('scroll', this.boundNavScrollHandler, { passive: true });
        window.addEventListener('resize', this.boundNavResizeHandler, { passive: true });
        this.checkNavOverflow();
      });
    },
    teardownNavOverflowHandling() {
      if (typeof window === 'undefined') return;
      if (this.navMainEl) {
        this.navMainEl.removeEventListener('scroll', this.boundNavScrollHandler);
      }
      if (this.boundNavResizeHandler) {
        window.removeEventListener('resize', this.boundNavResizeHandler);
      }
      this.navMainEl = null;
    },
    handleNavScroll() {
      this.updateNavBoundaries();
    },
    checkNavOverflow() {
      if (!this.navMainEl) return;
      const el = this.navMainEl;
      const epsilon = 1;
      const hasOverflow = el.scrollWidth - el.clientWidth > epsilon;
      this.navHasOverflow = hasOverflow;
      this.updateNavBoundaries();
    },
    updateNavBoundaries() {
      if (!this.navMainEl) return;
      const el = this.navMainEl;
      const epsilon = 1;
      this.navAtStart = el.scrollLeft <= epsilon;
      this.navAtEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - epsilon;
    },
    deferNavReflowCheck() {
      if (typeof window === 'undefined') return;
      window.requestAnimationFrame(() => {
        this.checkNavOverflow();
      });
    },
    scrollNav(direction = 1) {
      if (!this.navMainEl) return;
      const distance = 220 * Math.sign(direction || 1);
      this.navMainEl.scrollBy({
        left: distance,
        behavior: 'smooth'
      });
    }
  },
  watch: {
    isLoggedIn(newValue) {
      if (newValue) {
        this.startNotificationPolling();
      } else {
        this.stopNotificationPolling();
        this.unreadNotificationCount = 0; // Reset count on logout
      }
      this.deferNavReflowCheck();
    },
    currentLocale() {
      this.deferNavReflowCheck();
    }
  },
  mounted() {
    this.setupNavOverflowHandling();
  },
  // It can be beneficial to watch the $route to ensure isLoggedIn is re-evaluated
  // if navigation changes could imply an auth state change not caught by localStorage updates alone.
  // However, with localStorage as the source of truth for isLoggedIn, this should generally update.
  created() {
    // Start polling if logged in when the component is created
    if (this.isLoggedIn) {
      this.startNotificationPolling();
    }
    // Example of how to handle auth changes if an event bus was used (conceptual)
    // eventBus.$on('auth-change', this.handleAuthChangeForPolling);
  },
  beforeUnmount() {
    this.stopNotificationPolling();
    this.teardownNavOverflowHandling();
    // eventBus.$off('auth-change', this.handleAuthChangeForPolling);
  },
}
</script>

<style scoped>
.main-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 10;
  backdrop-filter: blur(18px);
  background: var(--color-surface-elevated);
  box-shadow: 0 12px 35px rgba(15, 23, 42, 0.08);
  border-bottom: 1px solid var(--color-border);
}

.topbar-inner {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  max-width: 100%;
  margin: 0 auto;
  padding: 0.65rem 1.5rem;
}

.brand {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  text-decoration: none;
  font-weight: 700;
  color: var(--color-text-primary);
  letter-spacing: -0.3px;
}

.brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, #60a5fa 0%, #2563eb 100%);
  color: #ffffff;
  font-weight: 600;
  box-shadow: 0 10px 25px rgba(37, 99, 235, 0.35);
}

.brand-text {
  font-size: 1.1rem;
}

.nav-main {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex: 1;
  justify-content: flex-start;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  min-width: 0;
  padding-bottom: 4px;
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.35) transparent;
  scroll-behavior: smooth;
  scroll-snap-type: x proximity;
}

.nav-main::-webkit-scrollbar {
  height: 6px;
}

.nav-main::-webkit-scrollbar-track {
  background: transparent;
}

.nav-main::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.35);
  border-radius: 999px;
}

.nav-main::before,
.nav-main::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 32px;
  pointer-events: none;
  transition: opacity 0.2s ease;
  opacity: 0;
  z-index: 1;
}

.nav-main::before {
  left: 0;
  background: linear-gradient(
    to right,
    var(--color-surface-elevated) 0%,
    rgba(255, 255, 255, 0)
  );
}

.nav-main::after {
  right: 0;
  background: linear-gradient(
    to left,
    var(--color-surface-elevated) 0%,
    rgba(255, 255, 255, 0)
  );
}

.nav-main--scrollable {
  padding-right: 12px;
}

.nav-main--scrollable:not(.nav-main--at-start)::before {
  opacity: 1;
}

.nav-main--scrollable:not(.nav-main--at-end)::after {
  opacity: 1;
}

.nav-container {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  gap: 0.25rem;
  min-width: 0;
}

.nav-container--scrollable {
  padding-inline: 0.25rem;
}

.nav-scroll {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(255, 255, 255, 0.85);
  color: rgba(71, 85, 105, 0.85);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
}

.nav-scroll:hover:not(:disabled) {
  border-color: rgba(59, 130, 246, 0.6);
  color: rgba(37, 99, 235, 0.9);
  background: rgba(255, 255, 255, 0.95);
}

.nav-scroll:disabled {
  opacity: 0.35;
  cursor: default;
  box-shadow: none;
}

.nav-scroll--left {
  order: -1;
}

.nav-main a {
  color: var(--color-text-secondary);
  text-decoration: none;
  padding: 0.45rem 0.7rem;
  border-radius: var(--radius-sm);
  font-weight: 600;
  font-size: 0.97rem;
  letter-spacing: 0.01em;
  white-space: nowrap;
  transition: background var(--transition-base), color var(--transition-base), box-shadow var(--transition-base), letter-spacing 0.2s ease;
  scroll-snap-align: start;
}

.nav-main a:hover {
  color: var(--color-text-primary);
  background: var(--color-primary-soft);
  box-shadow: 0 14px 30px rgba(59, 130, 246, 0.18);
  letter-spacing: 0.015em;
}

.nav-main a.router-link-exact-active {
  color: var(--color-primary-hover);
  background: var(--color-surface);
  box-shadow: 0 14px 32px rgba(59, 130, 246, 0.22);
  letter-spacing: 0.02em;
}

.nav-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
}

.promo-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.95rem;
  border-radius: 999px;
  background: linear-gradient(120deg, #f97316 0%, #f43f5e 45%, #ef4444 100%);
  background-size: 160% 160%;
  color: #ffffff;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 16px 34px rgba(239, 68, 68, 0.28), 0 0 0 1px rgba(255, 255, 255, 0.12) inset;
  white-space: nowrap;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  animation: promoGlow 6s ease infinite;
}

.promo-pill::after {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 18% 25%, rgba(255, 255, 255, 0.16), transparent 40%),
    radial-gradient(circle at 85% 15%, rgba(255, 255, 255, 0.12), transparent 45%);
  pointer-events: none;
}

.promo-pill__icon {
  font-size: 1rem;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.18));
}

.promo-pill__text {
  font-size: 0.93rem;
}

@keyframes promoGlow {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.language-switcher {
  display: flex;
  align-items: center;
}

.language-switcher select {
  padding: 0.3rem 0.55rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: 0.85rem;
  cursor: pointer;
  transition: border-color var(--transition-base), box-shadow var(--transition-base);
}

.language-switcher select:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.notification-link {
  position: relative;
  text-decoration: none;
  padding: 0.6rem 0.95rem;
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  transition: background var(--transition-base), color var(--transition-base), box-shadow var(--transition-base);
}

.notification-link:hover {
  color: var(--color-primary-hover);
  background: var(--color-surface);
  box-shadow: 0 12px 28px rgba(37, 99, 235, 0.18);
}

.notification-count {
  position: absolute;
  top: 6px;
  right: 6px;
  background: linear-gradient(135deg, #facc15 0%, #eab308 100%);
  color: var(--color-text-primary);
  border-radius: 999px;
  padding: 2px 6px;
  font-size: 0.7rem;
  font-weight: 600;
  box-shadow: 0 8px 16px rgba(234, 179, 8, 0.35);
}

.user-greeting {
  color: var(--color-text-secondary);
  font-weight: 500;
}

.profile-link {
  color: var(--color-text-secondary);
  text-decoration: none;
  padding: 0.55rem 0.85rem;
  border-radius: var(--radius-sm);
  background: rgba(229, 231, 235, 0.4);
  transition: background var(--transition-base), color var(--transition-base);
}

.profile-link:hover {
  color: var(--color-text-primary);
  background: rgba(148, 163, 184, 0.35);
}

.logout-button {
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: var(--radius-sm);
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  transition: transform var(--transition-base), box-shadow var(--transition-base);
  box-shadow: 0 14px 30px rgba(59, 130, 246, 0.25);
}

.logout-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 18px 38px rgba(59, 130, 246, 0.28);
}

.auth-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  text-decoration: none;
  padding: 0.65rem 1.35rem;
  border-radius: var(--radius-sm);
  background: linear-gradient(135deg, #38bdf8 0%, #3b82f6 100%);
  color: #ffffff;
  font-weight: 600;
  box-shadow: 0 14px 34px rgba(56, 189, 248, 0.3);
  transition: transform var(--transition-base), box-shadow var(--transition-base);
}

.auth-link:hover {
  transform: translateY(-1px);
  box-shadow: 0 18px 42px rgba(56, 189, 248, 0.34);
}

main {
  flex: 1;
  padding: 3rem 1.5rem 4rem;
  display: flex;
  justify-content: center;
}

main > * {
  width: 100%;
  max-width: 1200px;
}

.site-footer {
  margin-top: auto;
  background: var(--color-surface-elevated);
  border-top: 1px solid var(--color-border);
  box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.05);
}

.site-footer-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.75rem 2.5rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  color: var(--color-text-secondary);
}

.footer-copy {
  margin: 0;
}

.footer-links {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  align-items: center;
}

.footer-link {
  display: inline-flex;
  align-items: center;
  color: var(--color-text-secondary);
  text-decoration: none;
  font-weight: 500;
  line-height: 1.5;
  transition: color var(--transition-base);
}

.footer-link:hover {
  color: var(--color-primary-hover);
}

@media (max-width: 768px) {
  .topbar-inner {
    flex-wrap: wrap;
    gap: 1rem;
    padding: 0.85rem 1.5rem;
  }

  .nav-main {
    order: 3;
    width: 100%;
    justify-content: flex-start;
    flex-wrap: nowrap;
    overflow-x: auto;
  }

  .nav-right {
    width: 100%;
    justify-content: flex-end;
  }

  .promo-pill {
    order: 2;
    width: 100%;
    justify-content: center;
    text-align: center;
    white-space: normal;
    line-height: 1.45;
  }

  main {
    padding: 2.2rem 1.25rem 3rem;
  }
}
</style>
